import { motion, useReducedMotion } from 'framer-motion'
import Markdown from 'react-markdown'
import { Badge, Button, InstallSnippet } from '@mdxui/primitives'
import { HandHelping, Users, Zap } from 'lucide-react'
import type { HeroCenterFeaturesProps, FeatureItem } from './types'

import { cn } from '../../lib/cn'
import {
  sectionVariants,
  heroBadgeVariants,
  heroHeadlineVariants,
  heroDescriptionVariants,
  heroButtonsVariants,
  heroMediaVariants,
  gridItemVariants,
  gridContainerVariants,
  reducedMotionVariants,
} from '../../lib/motion'
import { BEHIND_NAVBAR_CLASSES } from '../../lib/constants'
import { CodeWindowHero } from '../code-window'

const defaultFeatures: FeatureItem[] = [
  {
    icon: <HandHelping className="h-auto w-5" />,
    title: 'Flexible Support',
    description:
      'Benefit from around-the-clock assistance to keep your business running smoothly.',
  },
  {
    icon: <Users className="h-auto w-5" />,
    title: 'Collaborative Tools',
    description:
      'Enhance teamwork with tools designed to simplify project management and communication.',
  },
  {
    icon: <Zap className="h-auto w-5" />,
    title: 'Lightning Fast Speed',
    description:
      'Experience the fastest load times with our high performance servers.',
  },
]

const HeroCenterFeatures = ({
  badge = 'mdxui.dev',
  showBadge = true,
  title = 'Build Beautiful Interfaces with MDXUI',
  subtitle,
  callToAction,
  callToActionUrl,
  secondaryCallToAction,
  secondaryCallToActionUrl,
  installSnippet,
  image = '/placeholders/placeholder-1.svg',
  mediaType = 'image',
  codeWindow,
  codeTheme = 'auto',
  features = defaultFeatures,
  showFeatures = true,
  background,
  behindNavbar = true,
  className,
}: Omit<HeroCenterFeaturesProps, 'variant'>) => {
  const prefersReducedMotion = useReducedMotion()

  const badgeAnimation = prefersReducedMotion ? reducedMotionVariants : heroBadgeVariants
  const headlineAnimation = prefersReducedMotion ? reducedMotionVariants : heroHeadlineVariants
  const descAnimation = prefersReducedMotion ? reducedMotionVariants : heroDescriptionVariants
  const buttonsAnimation = prefersReducedMotion ? reducedMotionVariants : heroButtonsVariants
  const mediaAnimation = prefersReducedMotion ? reducedMotionVariants : heroMediaVariants
  const itemAnimation = prefersReducedMotion ? reducedMotionVariants : gridItemVariants

  return (
    <div className={cn(
      'relative overflow-hidden',
      behindNavbar && BEHIND_NAVBAR_CLASSES,
      className
    )}>
      {background && (
        <div className="absolute inset-0">{background}</div>
      )}
      <motion.section
        className="relative z-10 py-32"
        variants={sectionVariants}
        initial="hidden"
        animate="visible"
      >
        <div className="container overflow-hidden mx-auto">
        <div className="mb-20 flex flex-col items-center gap-6 text-center max-w-5xl mx-auto">
          {showBadge && badge && (
            <motion.div variants={badgeAnimation}>
              <Badge variant="outline">{badge}</Badge>
            </motion.div>
          )}
          <motion.h1
            variants={headlineAnimation}
            className="text-4xl font-semibold text-balance lg:text-5xl"
          >
            {title}
          </motion.h1>
          {subtitle && (
            <motion.div
              variants={descAnimation}
              className="text-muted-foreground max-w-2xl text-lg [&_code]:rounded [&_code]:bg-muted [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:font-mono [&_code]:text-base [&_code]:text-foreground"
            >
              <Markdown>{subtitle}</Markdown>
            </motion.div>
          )}
          <motion.div
            variants={buttonsAnimation}
            className="flex w-full flex-col items-center justify-center gap-2 sm:flex-row"
          >
            {installSnippet ? (
              <InstallSnippet
                package={installSnippet.package}
                command={installSnippet.command}
                className="w-full sm:w-auto"
              />
            ) : (
              <>
                {callToAction && callToActionUrl && (
                  <Button asChild className="w-full sm:w-auto">
                    <a href={callToActionUrl}>{callToAction}</a>
                  </Button>
                )}
                {secondaryCallToAction && secondaryCallToActionUrl && (
                  <Button asChild variant="outline" className="w-full sm:w-auto">
                    <a href={secondaryCallToActionUrl}>{secondaryCallToAction}</a>
                  </Button>
                )}
              </>
            )}
          </motion.div>
        </div>
        <motion.div variants={mediaAnimation} className="mx-auto max-w-5xl pb-2">
          {mediaType === 'code' && codeWindow ? (
            <CodeWindowHero
              code={codeWindow.code}
              language={codeWindow.language}
              filename={codeWindow.filename}
              showHeader={true}
              theme={codeTheme}
              size="md"
              className="max-h-[500px] w-full"
            />
          ) : (
            <img
              src={image || '/placeholders/placeholder-1.svg'}
              alt={title}
              className="w-full rounded-[var(--radius-xl,calc(var(--radius)*1.5))]"
            />
          )}
        </motion.div>
        {showFeatures && features && features.length > 0 && (
          <motion.div
            className="mx-auto mt-10 max-w-5xl grid grid-cols-1 md:grid-cols-3 gap-8"
            variants={gridContainerVariants(0.1, 0.3)}
          >
            {features.map((feature) => (
              <motion.div
                key={feature.title}
                variants={itemAnimation}
                className="flex flex-col"
              >
                <div className="mb-4">{feature.icon}</div>
                <h3 className="mb-2 font-semibold">{feature.title}</h3>
                <p className="text-sm text-muted-foreground">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </motion.div>
        )}
      </div>
    </motion.section>
    </div>
  )
}

export { HeroCenterFeatures }
